import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HealthBadge from '../components/HealthBadge'
import { calendarFor, finishVarianceWorkingDays } from '../lib/calendar'
import { formatDays, formatIsoDate } from '../lib/format'
import type { ScheduleActivity, ScheduleCalendar, ScheduleDependency, ScheduleExternalActivity } from '../types'
import { GanttHoverStateContext } from './gantt-hover'

interface Props {
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  dependencies: ScheduleDependency[]
  /** Review fix (Important #3): needed to compute the baseline finish variance in working days,
   * matching `ScheduleStats.finishVarianceDays` and `DatesSection`. */
  calendarsById: Map<string, ScheduleCalendar>
  defaultCalendarId: string
}

const TOOLTIP_DELAY_MS = 300

/**
 * Fed by `GanttHoverContext`, not the store — the hover is transient view state.
 * Mounted once outside the scroller so it never scrolls with the timeline.
 */
const GanttTooltip: React.FC<Props> = ({ byId, externalById, dependencies, calendarsById, defaultCalendarId }) => {
  const { t } = useTranslation()
  const hover = useContext(GanttHoverStateContext)
  const [visible, setVisible] = useState(false)
  const dependencyById = useMemo(() => {
    const map = new Map<string, ScheduleDependency>()
    dependencies.forEach((d) => map.set(d.id, d))
    return map
  }, [dependencies])

  const delayKey = hover ? `${hover.kind}:${hover.id}` : null
  useEffect(() => {
    if (!delayKey) { setVisible(false); return undefined }
    setVisible(false)
    const timer = window.setTimeout(() => setVisible(true), TOOLTIP_DELAY_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayKey])

  if (!hover || !visible) return null

  const labelFor = (id: string): string => {
    const a = byId.get(id)
    if (a) return `${a.wbsCode} ${a.name}`
    const e = externalById.get(id)
    return e ? `${e.scheduleName} › ${e.name}` : id
  }

  let content: React.ReactNode = null
  if (hover.kind === 'activity') {
    const a = byId.get(hover.id)
    if (!a) return null
    // Review fix (Important #3): working days, not calendar days — matches the backend's
    // `ScheduleStats.finishVarianceDays` and the panel's `DatesSection`.
    const cal = calendarFor(a, calendarsById, defaultCalendarId)
    const variance = a.baselineFinish && cal ? finishVarianceWorkingDays(cal, a.baselineFinish, a.finish) : null
    content = (
      <>
        <div className="font-semibold">{a.wbsCode} {a.name}</div>
        <div className="tabular-nums">{formatIsoDate(t, a.start)} – {formatIsoDate(t, a.finish)}</div>
        <div>{t('Schedule_tooltip_duration')}: {formatDays(t, a.durationDays)}</div>
        <div>{t('Schedule_tooltip_float')}: {formatDays(t, a.totalFloatDays)}</div>
        <div>{t('Schedule_tooltip_progress')}: {a.isSummary ? a.rolledPercent : a.percentComplete}%</div>
        <div className="pt-1"><HealthBadge health={a.health} /></div>
        {a.constraintType !== 'ASAP' && (
          <div>{t('Schedule_tooltip_constraint')}: {t(`Schedule_constraint_${a.constraintType}`)}{a.constraintDate ? ` · ${formatIsoDate(t, a.constraintDate)}` : ''}</div>
        )}
        {a.baselineStart && a.baselineFinish && (
          <div>
            {t('Schedule_tooltip_baseline')}: {formatIsoDate(t, a.baselineStart)} – {formatIsoDate(t, a.baselineFinish)}
            {variance != null && <> · {t('Schedule_tooltip_variance')}: {variance > 0 ? '+' : ''}{variance} {t('Schedule_unit_days_short')}</>}
          </div>
        )}
        {a.warningCodes.length > 0 && (
          <ul className="list-disc pl-4 text-amber-700">
            {a.warningCodes.map((code) => <li key={code}>{t(`Schedule_warning_${code}`)}</li>)}
          </ul>
        )}
      </>
    )
  } else if (hover.kind === 'ghost') {
    const e = externalById.get(hover.id)
    if (!e) return null
    content = (
      <>
        <div className="font-semibold italic">{e.scheduleName} › {e.name}</div>
        <div className="tabular-nums">{formatIsoDate(t, e.start)} – {formatIsoDate(t, e.finish)}</div>
      </>
    )
  } else {
    const dep = dependencyById.get(hover.id)
    if (!dep) return null
    content = (
      <>
        <div className="font-semibold">{labelFor(dep.predecessorId)} → {labelFor(dep.successorId)}</div>
        <div>
          {t(`Schedule_dependency_${dep.type}`)}
          {dep.lagDays !== 0 && <> · {dep.lagDays > 0 ? '+' : ''}{dep.lagDays} {t('Schedule_unit_days_short')}</>}
        </div>
      </>
    )
  }

  return (
    <div
      className="fixed z-50 rounded-md border bg-popover px-3 py-2 text-xs shadow-md max-w-xs pointer-events-none space-y-0.5"
      // ui-drift: dynamic
      style={{ left: hover.x + 12, top: hover.y + 12 }}
    >
      {content}
    </div>
  )
}

export default GanttTooltip
